/**
 * @file Expenses — a landlord's cost against a BUILDING, the Schedule E-shaped
 * categories, and the property P&L (T1.10).
 *
 * ## There are TWO expense-shaped things in this product, and this file owns ONE
 *
 * PLAN.md §5 names both, in two different paragraphs:
 *
 *   1. **`expense_entries`** — the table this module is about. **Property-scoped**:
 *      `propertyId`, a `category`, `amountCents`, a `date`, a memo. It is an
 *      ordinary, EDITABLE record — the routes below ship PATCH and DELETE — because
 *      "I paid $412 for a water heater on the 3rd" is a fact about the world that a
 *      landlord typed in wrong and must be able to correct.
 *   2. **`ledger_entries.entry_type = 'expense'`** (migration 0006) —
 *      **lease-attributed**, a posting in the append-only tenant ledger, immutable
 *      for the row's whole life. Corrections are new rows with `correctionOf` set.
 *
 * They answer different questions ("what did this building cost me" vs "what is
 * posted against this tenancy") and have different lifecycles. This module does
 * NOT make `expense_entries` a view over the ledger and does NOT write ledger rows.
 * Migrations 0006 and 0010 each state the same distinction; `affectsTenantBalance`
 * (`./ledger.js`) is the ledger's half of it.
 *
 * ## The category list is a PUBLISHED STANDARD, not a product opinion
 *
 * `EXPENSE_CATEGORIES` is exactly the IRS Schedule E (Form 1040, Supplemental
 * Income and Loss) Part I expense lines 5–19, in the form's order: advertising (5),
 * auto and travel (6), cleaning and maintenance (7), commissions (8), insurance
 * (9), legal and professional fees (10), mortgage interest (11), other interest
 * (12), repairs (13), supplies (14), taxes (15), utilities (16), depreciation (17)
 * and other (19). The names are the form's, because a landlord filling in a
 * Schedule E recognises those words, and PLAN.md §5 itself asks for a
 * "Schedule-E-shaped" enum.
 *
 * **Nothing here expresses tax treatment.** The platform does not compute
 * depreciation, decide deductibility, split personal from business use, or import a
 * prior return. `depreciation` is a BUCKET a person may file a cost under, not a
 * figure the platform derives. That is stated in the API payload
 * (`PNL_BASIS_REASON`) rather than left for a reader to assume.
 *
 * ## The P&L is pure
 *
 * `summariseExpenses`, `propertyIncome` and `propertyPnl` take plain facts and
 * return plain numbers — no database, no request, no clock. The route loads rows
 * and hands them over; every figure on the screen is therefore testable without a
 * server, and the empty case is a unit test rather than a manual check.
 *
 * Money is INTEGER CENTS everywhere (CLAUDE.md / INV-MONEY-1) and never a float:
 * the sums below throw on a fractional amount rather than rounding it.
 *
 * The kernel discipline applies as everywhere in `packages/shared`: no drizzle, no
 * app code. The SPA imports these TYPES, the label map and the pure functions; the
 * API remains the authority on what may be written.
 */
import { z } from "zod";
import { countedLedgerEntries } from "./ledger.js";
import {
  chargeBalances,
  totalOutstandingCents,
  type ChargeLike,
  type PaymentEntryLike,
} from "./payments.js";

// ---------------------------------------------------------------------------
// The categories
// ---------------------------------------------------------------------------

/**
 * The fourteen IRS Schedule E Part I expense lines, in the form's order.
 *
 * Mirrored by the `expense_category` enum in
 * `packages/db/migrations/0010_expenses.sql`; a category added in one place and
 * not the other is refused by the enum at write time. The `label` is the form's
 * own wording, so a landlord filing a return sees the same list twice.
 *
 * The shape (`{ value, label }`) follows `PROPERTY_TYPES` and `UNIT_AMENITIES` in
 * `./properties.js`, and the list is deliberately short and closed: free text
 * gives you "repair", "repairs" and "R&M" for one fact, after which nothing can
 * total it — and totalling it is the ONLY thing the P&L does.
 */
export const EXPENSE_CATEGORIES = [
  { value: "advertising", label: "Advertising" },
  { value: "auto_and_travel", label: "Auto and travel" },
  { value: "cleaning_and_maintenance", label: "Cleaning and maintenance" },
  { value: "commissions", label: "Commissions" },
  { value: "insurance", label: "Insurance" },
  { value: "legal_and_professional_fees", label: "Legal and professional fees" },
  { value: "mortgage_interest", label: "Mortgage interest" },
  { value: "other_interest", label: "Other interest" },
  { value: "repairs", label: "Repairs" },
  { value: "supplies", label: "Supplies" },
  { value: "taxes", label: "Taxes" },
  { value: "utilities", label: "Utilities" },
  { value: "depreciation", label: "Depreciation" },
  { value: "other", label: "Other" },
] as const;

export type ExpenseCategory = (typeof EXPENSE_CATEGORIES)[number]["value"];

/** Mutable-tuple form, for `z.enum` (mirrors `PROPERTY_TYPE_VALUES`). */
export const EXPENSE_CATEGORY_VALUES = EXPENSE_CATEGORIES.map(
  (category) => category.value,
) as [ExpenseCategory, ...ExpenseCategory[]];

/**
 * Human labels for the same fourteen buckets.
 *
 * Here rather than in the SPA because the list, the filter chips and the P&L
 * breakdown all render them, and `cleaning_and_maintenance` appearing raw in a
 * totals table is how a category list starts disagreeing with itself.
 */
export const EXPENSE_CATEGORY_LABELS: Readonly<Record<ExpenseCategory, string>> =
  Object.fromEntries(
    EXPENSE_CATEGORIES.map((category) => [category.value, category.label]),
  ) as Record<ExpenseCategory, string>;

/**
 * Why the P&L is what it is, in one sentence, for the API payload and the screen.
 *
 * A rendered "Net $4,120" invites a person to read it as profit or as a tax
 * figure. It is neither: it is money RECORDED as received against this building
 * minus the costs RECORDED against it, over the rows that exist. Saying so is the
 * difference between a useful summary and a plausible one.
 */
export const PNL_BASIS_REASON =
  "a cash-basis summary of the rows recorded in this portfolio: rent charged, " +
  "money received, and the costs entered against this building. It is not a tax " +
  "return — the platform computes no depreciation, decides no deductibility, and " +
  "knows nothing about a period before the records began.";

// ---------------------------------------------------------------------------
// Input schemas
// ---------------------------------------------------------------------------

/**
 * Optional text stored as `null` when absent or blank — the same normalisation
 * `properties.ts`, `leases.ts`, `ledger.ts` and `maintenance.ts` use, and for the
 * same reason: a cleared form field must not persist as `""`, or "no memo" gets
 * two representations and every later query has to remember both.
 */
function optionalText(max: number) {
  return z
    .string()
    .trim()
    .max(max)
    .nullish()
    .transform((value) =>
      value === undefined || value === null || value.length === 0 ? null : value,
    );
}

/** `YYYY-MM-DD`, as the columns store it. */
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/**
 * A date, or blank for "not given".
 *
 * `refine` and NOT `.regex()`, for the reason `packages/shared/src/properties.ts`
 * documents at `purchaseDate`: a `.regex()` rejects `""` with "must be a
 * YYYY-MM-DD date", so a caller clearing (or simply not filling) the field would
 * be told they typed something wrong. The `refine` lets the blank through the
 * format check and the transform turns it into the `null` the database stores.
 */
const optionalDate = z
  .string()
  .trim()
  .refine((value) => value === "" || DATE_PATTERN.test(value), "must be a YYYY-MM-DD date")
  .nullish()
  .transform((value) =>
    value === undefined || value === null || value.length === 0 ? null : value,
  );

/**
 * The fields a new expense carries.
 *
 * NOT accepted here, each for a reason:
 *
 *   * `receiptFileId` — **it IS accepted, and it was not before (T0.8 Phase B).**
 *     This comment used to say "there is no upload route and no file UI in this
 *     repo … a body that could assert a receipt id would let a caller name a file
 *     nobody can open" (migration 0010's header). Both halves now exist: `POST
 *     /workspaces/:id/documents?domain=expense` stores one, `GET
 *     …/documents/:documentId` serves it back and writes the `audit_log` row, and
 *     `AttachmentField` is the picker. So `expense_entries.receipt_file_id` (0010)
 *     gets its writer. It is nullable — a landlord who paid cash has no receipt, and
 *     demanding one would refuse to record a real cost — and the id is verified by
 *     the ROUTE against `documents` in this workspace with the `expense` domain
 *     (`apps/api/src/domain/documents.ts`), which zod cannot do because it is a
 *     question about rows.
 *   * `createdBy` — the route records who wrote the row; a body cannot.
 *
 * `amountCents` is a non-negative integer number of cents, NOT a dollar string:
 * the SPA converts what a person types with `parseDollarInput` (the kernel's ONE
 * conversion, in `./constants.js`) and never sends a float. Note this is the
 * opposite convention from `ledger_entries.amount_cents`, which is signed — see the
 * file header.
 */
const expenseFieldsSchema = z.object({
  /** The BUILDING the cost was incurred on. Must be a property of this workspace. */
  propertyId: z.string().uuid("must be a property id"),
  /** One of the fourteen Schedule E buckets, required — an untotalled cost is unusable. */
  category: z.enum(EXPENSE_CATEGORY_VALUES),
  amountCents: z
    .number()
    .int("money must be whole cents — no decimals")
    .min(0, "cannot be negative"),
  /** The calendar day the cost was incurred. Required on a create — see below. */
  date: optionalDate,
  memo: optionalText(500),
  /**
   * The receipt, as a `documents` id in the `expense` domain, or `null` for a cost
   * with nothing on paper. `null` CLEARS the link on a PATCH; leaving the key out
   * leaves the column alone.
   */
  receiptFileId: z
    .string()
    .uuid("must be a document id")
    .nullish()
    .transform((value) => value ?? null),
});

/**
 * POST /workspaces/:workspaceId/expenses body.
 *
 * `date` is required here even though the field normalises a blank to `null`: an
 * expense is a cost ON A DAY, and the P&L's per-category totals are meaningless
 * without it. The refusal names the field rather than silently defaulting to today,
 * which would record a date nobody asserted.
 */
export const expenseEntryCreateSchema = expenseFieldsSchema.superRefine((value, ctx) => {
  if (value.date === null) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["date"],
      message: "is required — when was this cost incurred?",
    });
  }
});

/**
 * PATCH /workspaces/:workspaceId/expenses/:expenseId body.
 *
 * `.partial()` on the same field set, so a field added to create is updatable by
 * default and cannot be forgotten — the rule `properties.ts` states for its own
 * update schema. An expense is an ORDINARY RECORD, unlike a ledger row: the
 * append-only trigger that forbids editing `ledger_entries` does not apply here,
 * which is the whole reason this PATCH exists and the ledger's does not.
 *
 * `date` may not be CLEARED. The column is NOT NULL, and a cost with no day cannot
 * be placed in a year — so an explicit blank is refused with a sentence instead of
 * reaching the database.
 */
export const expenseEntryUpdateSchema = expenseFieldsSchema
  .partial()
  .refine(
    (value) => value.date !== null,
    "an expense is a cost on a day, so its date cannot be cleared",
  );

export type ExpenseEntryCreateInput = z.infer<typeof expenseEntryCreateSchema>;
export type ExpenseEntryUpdateInput = z.infer<typeof expenseEntryUpdateSchema>;

/** The PRE-parse shapes, for the SPA to build request bodies from. */
export type ExpenseEntryCreateBody = z.input<typeof expenseEntryCreateSchema>;
export type ExpenseEntryUpdateBody = z.input<typeof expenseEntryUpdateSchema>;

// ---------------------------------------------------------------------------
// The P&L — pure functions over facts
// ---------------------------------------------------------------------------

/**
 * Integer-only addition. Money is INTEGER CENTS (CLAUDE.md); a fractional amount
 * is a bug that has already happened upstream, so it throws here rather than being
 * silently truncated into a report. The ledger module's `sumCents` states the same
 * rule for the same reason.
 */
function sumCents(values: readonly number[]): number {
  let total = 0;
  for (const value of values) {
    if (!Number.isInteger(value)) {
      throw new Error(
        `expenses: ${String(value)} is not a whole number of cents — money is INTEGER CENTS (CLAUDE.md)`,
      );
    }
    total += value;
  }
  return total;
}

/**
 * The fields the P&L reads from an expense row.
 *
 * Structural rather than a class, so the API's drizzle rows, the SPA's wire type
 * and a test literal all satisfy it without a conversion.
 */
export interface ExpenseLike {
  readonly propertyId: string;
  readonly category: ExpenseCategory;
  /** Non-negative integer cents. */
  readonly amountCents: number;
  /** `YYYY-MM-DD` — the day the cost was incurred. */
  readonly date: string;
}

/** One Schedule E bucket, with the rows behind it. */
export interface ExpenseCategoryTotal {
  readonly category: ExpenseCategory;
  readonly entries: number;
  readonly totalCents: number;
}

/**
 * The Schedule E-shaped summary of a set of expenses.
 *
 * `byCategory` carries **only the buckets that actually occur**, in Schedule E line
 * order — the same rule the units, leases, charges and maintenance lists apply to
 * their breakdowns: a portfolio that has never recorded a commission must not
 * render "Commissions $0.00", because a zero in a totals table reads as a fact
 * about the world rather than about the rows.
 *
 * The empty case is `{ entries: 0, totalCents: 0, byCategory: [] }`. That is
 * arithmetic, not a claim: it is the SCREEN's job not to render `$0.00` when
 * nothing has been recorded at all (`apps/web/src/components/ExpensesScreen.tsx`).
 */
export function summariseExpenses(expenses: readonly ExpenseLike[]): {
  readonly entries: number;
  readonly totalCents: number;
  readonly byCategory: readonly ExpenseCategoryTotal[];
} {
  const byCategory = new Map<ExpenseCategory, number[]>();
  for (const expense of expenses) {
    const bucket = byCategory.get(expense.category);
    if (bucket === undefined) byCategory.set(expense.category, [expense.amountCents]);
    else bucket.push(expense.amountCents);
  }

  return {
    entries: expenses.length,
    totalCents: sumCents(expenses.map((expense) => expense.amountCents)),
    byCategory: EXPENSE_CATEGORIES.map((category) => category.value)
      .filter((category) => byCategory.has(category))
      .map((category) => {
        const amounts = byCategory.get(category) ?? [];
        return {
          category,
          entries: amounts.length,
          totalCents: sumCents(amounts),
        };
      }),
  };
}

/** The facts a property's INCOME side is computed from. Facts, not rows. */
export interface PropertyIncomeFacts {
  /** The property's rent charges — the OBLIGATION for each period (§6.1). */
  readonly charges: readonly ChargeLike[];
  /**
   * The ledger rows that could be applied to those charges.
   *
   * Deliberately the property's rows only, and unfiltered by type:
   * `countedLedgerEntries` needs the whole correction chain to decide what counts,
   * and a correction of a payment is routinely an `adjustment`.
   */
  readonly entries: readonly PaymentEntryLike[];
}

/**
 * What a property has been charged and what has been received against it.
 *
 * Three figures, and the separation is the point:
 *
 *   * `chargedCents` is what the leases on this building were BILLED — the sum of
 *     the `rent_charges` rows. It is an obligation, not money.
 *   * `settledCents` and `pendingCents` are money RECEIVED, split by §7.2's
 *     lifecycle, because an uncleared check is not money in the bank. Both count
 *     only rows that name a charge of THIS property: money attributed to no charge
 *     is not attributed to this building either, and the route reports it
 *     separately rather than assigning it to a property by guesswork.
 *   * `outstandingCents` is the per-charge remainder (`totalOutstandingCents`), so
 *     an overpayment on one period never cancels another period's debt.
 *
 * Every figure is computed by the SHARED rules — `countedLedgerEntries`,
 * `chargeBalances`, `totalOutstandingCents` — never re-derived here.
 */
export interface PropertyIncomeSummary {
  readonly charges: number;
  readonly chargedCents: number;
  readonly settledCents: number;
  readonly pendingCents: number;
  readonly outstandingCents: number;
}

/** The charges of ONE property and the money that could be applied to them. */
export function propertyIncome(facts: PropertyIncomeFacts): PropertyIncomeSummary {
  const balances = chargeBalances(facts.charges, facts.entries);
  const chargeIds = new Set(facts.charges.map((charge) => charge.id));

  let settledCents = 0;
  let pendingCents = 0;
  for (const entry of countedLedgerEntries(facts.entries)) {
    if (entry.entryType !== "payment") continue;
    if (entry.rentChargeId === null || !chargeIds.has(entry.rentChargeId)) continue;
    // Payments are NEGATIVE on the ledger (they reduce what is owed); this view
    // reports money in, so the sign is dropped here and nowhere else — the same
    // rule `paymentsTotalCents` follows for the portfolio.
    const magnitude = Math.abs(entry.amountCents);
    if (entry.paymentState === "settled") settledCents += magnitude;
    else if (entry.paymentState === "pending") pendingCents += magnitude;
  }

  return {
    charges: facts.charges.length,
    chargedCents: sumCents(facts.charges.map((charge) => charge.amountCents)),
    settledCents,
    pendingCents,
    outstandingCents: totalOutstandingCents(balances),
  };
}

/** The facts the P&L is computed from. Plain rows, no live objects. */
export interface PropertyPnlFacts {
  /** The building this P&L is about. */
  readonly propertyId: string;
  readonly income: PropertyIncomeFacts;
  /**
   * Expense rows to consider. The function filters them to `propertyId` itself, so
   * a caller that hands over a wider set — the portfolio list, say — cannot quietly
   * add another building's costs to this one's total.
   */
  readonly expenses: readonly ExpenseLike[];
}

/**
 * Payment rows that name NO charge, so no building can claim them.
 *
 * The honest edge of the P&L. Income is attributed to a property through a
 * payment's `rent_charge_id`: charge → lease → unit → building. A payment row whose
 * link is NULL cannot be attributed to any building, and there are only two
 * dishonest things to do with it — divide it among properties by guesswork, or drop
 * it silently and let the totals look complete. This reports it instead, as a COUNT
 * and an amount, and the P&L screen renders nothing while the count is zero. (The
 * recording route always names a charge, so it IS zero on every portfolio this
 * build can produce — which is exactly why it must be counted rather than assumed.)
 *
 * Counted rows only, through the ONE balance rule, so a corrected payment is not
 * double counted.
 */
export function unattributedPaymentTotals(entries: readonly PaymentEntryLike[]): {
  readonly payments: number;
  readonly receivedCents: number;
} {
  let payments = 0;
  let receivedCents = 0;
  for (const entry of countedLedgerEntries(entries)) {
    if (entry.entryType !== "payment") continue;
    if (entry.rentChargeId !== null) continue;
    payments += 1;
    receivedCents += Math.abs(entry.amountCents);
  }
  return { payments, receivedCents };
}

/**
 * **The basic property P&L**, as one pure function.
 *
 * Income is `propertyIncome` (real `rent_charges` rows and real counted payment
 * rows, through the shared balance rules). Expenses are `summariseExpenses`. The
 * two are added, not netted into one number, because they are different claims:
 * "charged" is an obligation, "received" is money, and "spent" is a cost.
 *
 * `netRecordedCents` is deliberately named for what it is: money RECEIVED less the
 * costs RECORDED, over the rows that exist. It is not profit — the platform has no
 * figure for vacancy, no mortgage schedule beyond what was entered, no depreciation
 * calculation, and no knowledge of a period before the records began (call it
 * profit and a landlord will file it). See `PNL_BASIS_REASON`.
 *
 * It is NOT `null` on an empty set: `0 - 0` is arithmetic. Whether a `$0.00` may be
 * RENDERED is the screen's decision, and the screen may not.
 */
export interface PropertyPnl {
  readonly income: PropertyIncomeSummary;
  readonly expenses: {
    readonly entries: number;
    readonly totalCents: number;
    readonly byCategory: readonly ExpenseCategoryTotal[];
  };
  /** money received (settled) − costs recorded. See the note above. */
  readonly netRecordedCents: number;
}

export function propertyPnl(facts: PropertyPnlFacts): PropertyPnl {
  const income = propertyIncome(facts.income);
  // Scoped HERE, not by hope: the income side arrives already scoped (the charges
  // are this building's), so the expense side is made to agree with it rather than
  // trusting every caller to have filtered.
  const expenses = summariseExpenses(
    facts.expenses.filter((expense) => expense.propertyId === facts.propertyId),
  );

  return {
    income,
    expenses,
    // PENDING money is deliberately NOT subtracted into the net: an uncleared check
    // has not been received, and netting it would report money the landlord does not
    // have. It is reported beside the net instead.
    netRecordedCents: income.settledCents - expenses.totalCents,
  };
}

// ---------------------------------------------------------------------------
// DTOs — what the API returns
// ---------------------------------------------------------------------------

/**
 * One expense, as the API returns it.
 *
 * Declared here rather than inferred from the drizzle schema so the kernel does
 * not depend on a database package (dependency direction: apps → packages). The
 * amount is a non-negative integer number of cents and the date is a `YYYY-MM-DD`
 * calendar date — never an instant.
 */
export interface ExpenseEntryDto {
  readonly id: string;
  readonly propertyId: string;
  readonly category: ExpenseCategory;
  /** Non-negative integer cents. */
  readonly amountCents: number;
  /** The calendar day the cost was incurred, `YYYY-MM-DD`. */
  readonly date: string;
  /**
   * §5's receipt, or `null` for a cost with nothing on paper. A `documents` row of
   * the `expense` domain, verified by the route before it is written (T0.8 Phase B);
   * the bytes are fetched through `GET …/documents/:documentId`, which writes the
   * audit row and is the only way to read a stored file.
   */
  readonly receiptFileId: string | null;
  readonly memo: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

/**
 * An expense plus the context that makes it identifiable: the building it was
 * incurred on.
 *
 * The Expenses screen answers "what has this portfolio cost me, and where", so
 * each row names its building without a request per row — the same reasoning as
 * `MaintenanceListRowDto`.
 */
export interface ExpenseListRowDto extends ExpenseEntryDto {
  readonly propertyLabel: string | null;
  readonly addressLine1: string;
  readonly city: string;
  readonly state: string;
}

/**
 * GET /workspaces/:workspaceId/expenses — every expense in the portfolio.
 *
 * `counts` and `byCategory` are folded from the rows returned beside them, so every
 * figure can be checked against the list. There is deliberately **no income and no
 * net** here: those are per-BUILDING questions and belong to the P&L endpoint,
 * where the property is named.
 */
export interface ExpenseListDto {
  /** True when the list was cut at the server cap: these are the first rows, not all of them. */
  readonly truncated?: true;
  readonly counts: {
    readonly expenses: number;
    /** Distinct buildings with at least one expense — not the count of expenses. */
    readonly properties: number;
    readonly totalCents: number;
  };
  /** Only the categories that actually occur, in Schedule E line order. */
  readonly byCategory: readonly {
    readonly category: ExpenseCategory;
    readonly entries: number;
    readonly totalCents: number;
  }[];
  /** Newest first: the date the cost was incurred, then when it was written. */
  readonly expenses: readonly ExpenseListRowDto[];
}

/**
 * GET /workspaces/:workspaceId/properties/:propertyId/pnl — one building's basic
 * P&L.
 *
 * `income` is real: rent charges that exist, and counted payment rows attributed to
 * this building through them. `expenses` is what was entered. `netRecordedCents` is
 * the difference, and `basis` says in one sentence what it is and is not.
 *
 * `unattributed` is the honest edge. A payment row that names no charge cannot be
 * attributed to any building, so it is reported as a COUNT and an amount here
 * rather than divided among properties by guesswork or silently dropped. It is zero
 * on every portfolio whose payments came through the recording route (which
 * requires a charge), and the screen renders nothing while it is zero.
 */
export interface PropertyPnlDto {
  readonly propertyId: string;
  readonly propertyLabel: string | null;
  readonly addressLine1: string;
  readonly city: string;
  readonly state: string;
  readonly income: PropertyIncomeSummary;
  readonly expenses: {
    readonly entries: number;
    readonly totalCents: number;
    readonly byCategory: readonly ExpenseCategoryTotal[];
  };
  readonly netRecordedCents: number;
  /** The sentence that stops the net being read as profit or as a tax figure. */
  readonly basis: string;
  readonly unattributed: {
    readonly payments: number;
    readonly receivedCents: number;
  };
}
